import { useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { EyeOff, Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PWD_OK = (p) => p.length >= 20 && /^[A-Za-z0-9]+$/.test(p) && /[A-Za-z]/.test(p) && /\d/.test(p);

// Iter 142: "8 ore MAX" — attivazione con password ≥20 alfanumerica, disattivazione solo con la stessa password
export function CapOttoOreCard({ settings, setSettings }) {
  const on = !!settings.global_hours_cap_enabled;
  const [mode, setMode] = useState(null); // "on" | "off" | null
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const open = (v) => { setPwd(""); setPwd2(""); setError(""); setMode(v ? "on" : "off"); };
  const close = () => { if (!busy) setMode(null); };

  const submit = async () => {
    setError("");
    if (mode === "on") {
      if (!PWD_OK(pwd)) return setError("Password non valida: almeno 20 caratteri, solo lettere e numeri, con almeno una lettera e un numero.");
      if (pwd !== pwd2) return setError("Le due password non coincidono.");
    } else if (!pwd) {
      return setError("Inserisci la password impostata all'attivazione.");
    }
    setBusy(true);
    try {
      await api.put("/settings", { ...settings, global_hours_cap_enabled: mode === "on", cap_password: pwd });
      setSettings({ ...settings, global_hours_cap_enabled: mode === "on" });
      toast.success(mode === "on" ? "8 ore MAX attivata: da ora nessuno vede più di 8 ore al giorno" : "8 ore MAX disattivata: ore normali ripristinate");
      setMode(null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 space-y-3 border-amber-200" data-testid="cap-otto-ore-card">
      <div className="flex items-center justify-between gap-3">
        <div className="font-heading font-bold tracking-tight flex items-center gap-2">
          <EyeOff className="h-4 w-4" /> 8 ore MAX
        </div>
        <Switch checked={on} onCheckedChange={open} data-testid="global-hours-cap-switch" />
      </div>
      {on && (
        <div className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-center gap-2" data-testid="cap-otto-ore-active-banner">
          <Lock className="h-3.5 w-3.5 shrink-0" /> ATTIVA: nessuno vede più di 8 ore al giorno per dipendente. Per disattivare serve la password.
        </div>
      )}

      <Dialog open={!!mode} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-lg" data-testid="cap-otto-ore-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> {mode === "on" ? "Attivare 8 ore MAX?" : "Disattivare 8 ore MAX"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-sm text-slate-700 space-y-2 pt-1">
                {mode === "on" ? (
                  <>
                    <p><b>Cosa comporta l'attivazione:</b></p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Tutti gli utenti, <b>admin compresi</b>, vedono al massimo <b>8 ore al giorno per dipendente</b> ovunque: liste, riepiloghi, stampe, PDF, Excel, email, dashboard e statistiche.</li>
                      <li>Le ore si inseriscono solo intere; sabato, domenica e festivi risultano a 0.</li>
                      <li>Per <b>tornare alle ore normali serve per forza la password</b> che imposti adesso: non esiste nessun'altra via.</li>
                      <li className="text-red-700 font-bold">Se la password viene smarrita, le ore oltre le 8 sono perse: nessuno potrà più vederle o recuperarle.</li>
                    </ul>
                    <p className="text-xs text-slate-500">Password: almeno 20 caratteri, solo lettere e numeri. Scrivila e conservala in un luogo sicuro.</p>
                  </>
                ) : (
                  <p>Inserisci la password impostata all'attivazione. Senza quella password <b>8 ore MAX resta attiva</b>.</p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={mode === "on" ? "Nuova password (min. 20 caratteri alfanumerici)" : "Password"} autoComplete="off" className="h-12 font-mono" data-testid="cap-otto-ore-password-input" />
            {mode === "on" && (
              <>
                <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} placeholder="Ripeti la password" autoComplete="off" className="h-12 font-mono" data-testid="cap-otto-ore-password-confirm-input" />
                <div className="text-xs text-slate-500" data-testid="cap-otto-ore-password-len">{pwd.length}/20 caratteri {PWD_OK(pwd) ? "✓" : ""}</div>
              </>
            )}
            {error && <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="cap-otto-ore-error">{error}</div>}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close} disabled={busy} data-testid="cap-otto-ore-cancel">Annulla</Button>
            <Button onClick={submit} disabled={busy} className={mode === "on" ? "bg-amber-600 hover:bg-amber-700" : "bg-[#0044FF] hover:bg-[#0033CC]"} data-testid="cap-otto-ore-confirm">
              {busy ? "Attendere…" : mode === "on" ? "Ho capito, attiva 8 ore MAX" : "Disattiva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
